import { create } from "zustand";

import { getMe, updateSettings, type Me, type Settings } from "../lib/api";

/**
 * Часовой пояс устройства в минутах к UTC (Москва: +180). Телефон его знает,
 * поэтому бот получает пояс молча — вместо вопроса на старте, который никому
 * не нужен. Без пояса напоминания не отправляются вовсе.
 */
const deviceOffset = (): number => -new Date().getTimezoneOffset();

type Status = "loading" | "ready" | "error";

interface UserState {
  me: Me | null;
  status: Status;
  error: string | null;
  load: () => Promise<void>;
  apply: (patch: Settings) => Promise<void>;
}

export const useUser = create<UserState>((set) => ({
  me: null,
  status: "loading",
  error: null,

  async load() {
    set({ status: "loading", error: null });
    try {
      let me = await getMe();
      // Переезд в другой пояс — обычное дело, поэтому сверяем каждый вход.
      // Не дошло — не беда: настройка догонит при следующем запуске.
      if (me.tz_offset_min !== deviceOffset()) {
        me = await updateSettings({ tz_offset_min: deviceOffset() }).catch(() => me);
      }
      set({ me, status: "ready" });
    } catch (e) {
      set({ status: "error", error: e instanceof Error ? e.message : String(e) });
    }
  },

  /**
   * Сохранить настройку и обновить профиль на месте.
   *
   * Не через `load()`: тот переводит статус в «loading», приложение показывает
   * загрузку и пересоздаёт экран — человек, тронув выключатель, улетает в
   * начало страницы. Ответ PATCH и есть готовый профиль, его и кладём.
   */
  async apply(patch: Settings) {
    set({ me: await updateSettings(patch) });
  },
}));
